<div class="mx-auto max-w-(--breakpoint-2xl)">
  <div class="mb-6">
    <h1 class="text-title-sm font-semibold text-gray-800 dark:text-white/90">Payment Remediation</h1>
    <p class="mt-1 text-theme-sm text-gray-500 dark:text-gray-400">
      Confirmed events whose payment schedule currently looks wrong. The list runs live and clears itself as
      cases are fixed, approved as an override, or reassigned.
    </p>
  </div>

  <!-- TVG-201: plain-language explainer, collapsed by default so it never takes up
       space until finance chooses to open it. -->
  <div class="mb-6 rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
    <button
      type="button"
      (click)="toggleExplainer()"
      [attr.aria-expanded]="isExplainerOpen"
      aria-controls="remediation-explainer"
      class="flex w-full items-center justify-between gap-3 rounded-xl px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-white/[0.02]">
      <span class="flex items-center gap-2">
        <lucide-icon [name]="GhostIcon" [size]="18" class="text-brand-500"></lucide-icon>
        <span class="text-theme-sm font-medium text-gray-800 dark:text-white/90">
          What is this page? How do I read a row?
        </span>
      </span>
      <lucide-icon [name]="isExplainerOpen ? ChevronUp : ChevronDown" [size]="20" class="shrink-0 text-gray-500 dark:text-gray-400"></lucide-icon>
    </button>

    @if (isExplainerOpen) {
      <div id="remediation-explainer" class="space-y-6 border-t border-gray-200 px-5 py-5 text-theme-sm leading-relaxed text-gray-600 dark:border-gray-800 dark:text-gray-300">
        <section>
          <h3 class="mb-1.5 text-theme-sm font-semibold text-gray-800 dark:text-white/90">What this page is for</h3>
          <p>
            This page lists confirmed weddings whose payment schedule currently looks wrong — the amount recorded
            against the event does not match what the system calculates it should be. Each row is one event that needs
            a decision. The list runs live: as soon as a case is fixed, approved, or reassigned it stops meeting the
            criteria and drops off on its own. A healthy event never appears here at all.
          </p>
        </section>

        <section>
          <h3 class="mb-1.5 text-theme-sm font-semibold text-gray-800 dark:text-white/90">What went wrong in the first place</h3>
          <p>
            A wedding's payments are worked out as a <strong>waterfall</strong>: four scheduled payments — Reservation
            Fee, First Payment, Second Payment and Final Payment — are calculated in order. If a couple overpays an
            earlier payment, that surplus is meant to <em>cascade</em> down and reduce the next one. An earlier bug meant
            that once the Final Payment had been marked as fully paid, the system stopped rewriting its amount. So when a
            surplus from an earlier stage should have cascaded into the Final Payment and lowered it, the reduction never
            landed — the stored figure kept showing its original, pre-credit amount. On top of that, some figures were
            later corrected by hand, and in a few cases money was keyed onto a payment row that isn't part of the
            standard four-row schedule, where the calculation never looks. Those three situations are exactly what this
            page surfaces.
          </p>
        </section>

        <section>
          <h3 class="mb-2 text-theme-sm font-semibold text-gray-800 dark:text-white/90">The three strands</h3>
          <dl class="space-y-2">
            <div>
              <dt class="font-medium text-gray-800 dark:text-white/90">{{ strandLabels.cascade_not_absorbed }}</dt>
              <dd>
                The stored Final Payment still equals its original, pre-credit amount. A surplus from an earlier stage
                should have cascaded into it and lowered it, but the calculation never re-ran after that credit arrived,
                so it was never absorbed. These are safe to <strong>Fix</strong>.
              </dd>
            </div>
            <div>
              <dt class="font-medium text-gray-800 dark:text-white/90">{{ strandLabels.hand_edited }}</dt>
              <dd>
                The stored Final Payment matches neither the calculated figure nor the original pre-credit amount, which
                means someone typed it in by hand. This needs a human decision: either <strong>Fix</strong> it back to
                the calculated figure, or <strong>Approve</strong> the hand-typed figure as correct and intended.
              </dd>
            </div>
            <div>
              <dt class="font-medium text-gray-800 dark:text-white/90">{{ strandLabels.ghost_row }}</dt>
              <dd>
                Money has been keyed onto a payment row that isn't one of the four standard schedule rows. The
                calculation never sees that row, so a plain Fix can't correct it — the money has to be
                <strong>Reassigned</strong> onto a standard row first.
              </dd>
            </div>
          </dl>
        </section>

        <section>
          <h3 class="mb-2 text-theme-sm font-semibold text-gray-800 dark:text-white/90">What the columns mean</h3>
          <dl class="space-y-2">
            <div>
              <dt class="font-medium text-gray-800 dark:text-white/90">Stored</dt>
              <dd>The amount currently recorded against the event, as it stands today.</dd>
            </div>
            <div>
              <dt class="font-medium text-gray-800 dark:text-white/90">Calculated</dt>
              <dd>What the system works out the correct amount should be from the event's actual data.</dd>
            </div>
            <div>
              <dt class="font-medium text-gray-800 dark:text-white/90">Difference</dt>
              <dd>The gap between the two (Stored minus Calculated) — this is what needs resolving. A difference of £0.00 means they agree.</dd>
            </div>
          </dl>
        </section>

        <section>
          <h3 class="mb-2 text-theme-sm font-semibold text-gray-800 dark:text-white/90">Worked examples</h3>
          <div class="space-y-3">
            <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-white/[0.02]">
              <p class="mb-1 font-medium text-gray-800 dark:text-white/90">A flagged row — ghost row</p>
              <p class="mb-2">
                Say an event shows <strong>Stored £1,500.00</strong>, <strong>Calculated £0.00</strong>,
                <strong>Difference £1,500.00</strong>. It is flagged because that £1,500 was keyed onto a payment row
                that the schedule calculation never counts — so as far as the calculation is concerned, that row should
                be £0.00. The money is real; it is just sitting in the wrong place. The fix is to
                <strong>Reassign</strong> it onto a standard schedule row.
              </p>
            </div>
            <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-white/[0.02]">
              <p class="mb-1 font-medium text-gray-800 dark:text-white/90">A normal event — for comparison</p>
              <p>
                A healthy event's stored Final Payment equals the calculated figure, so <strong>Stored</strong> and
                <strong>Calculated</strong> match and the <strong>Difference is £0.00</strong> — there is nothing to
                resolve. That is why a correct event never appears on this list: this page only shows events where
                Stored and Calculated disagree.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 class="mb-2 text-theme-sm font-semibold text-gray-800 dark:text-white/90">What each action does</h3>
          <dl class="space-y-2">
            <div>
              <dt class="font-medium text-gray-800 dark:text-white/90">Fix</dt>
              <dd>Re-runs the calculation and writes the corrected figure back to the schedule. Use it on cascade and hand-edited rows you want reset to the calculated amount.</dd>
            </div>
            <div>
              <dt class="font-medium text-gray-800 dark:text-white/90">Approve</dt>
              <dd>Records the stored, hand-typed figure as correct and intended, so the event drops off the list without being changed. Use it when a hand-edit was deliberate and right.</dd>
            </div>
            <div>
              <dt class="font-medium text-gray-800 dark:text-white/90">Reassign</dt>
              <dd>Moves ghost-row money onto a standard schedule row and then re-runs the calculation. Use it for ghost rows, where a plain Fix can't help.</dd>
            </div>
          </dl>
        </section>
      </div>
    }
  </div>

  <!-- TVG-176: per-strand counts and overall total, so the backlog is visible at a glance. -->
  @if (response) {
    <div class="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
      <div class="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-theme-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Total remaining</p>
        <p class="mt-1 text-title-sm font-semibold text-gray-800 dark:text-white/90">{{ response.total }}</p>
      </div>
      <div class="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-theme-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ strandLabels.cascade_not_absorbed }}</p>
        <p class="mt-1 text-title-sm font-semibold text-gray-800 dark:text-white/90">{{ response.strand_counts.cascade_not_absorbed }}</p>
      </div>
      <div class="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-theme-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ strandLabels.hand_edited }}</p>
        <p class="mt-1 text-title-sm font-semibold text-gray-800 dark:text-white/90">{{ response.strand_counts.hand_edited }}</p>
      </div>
      <div class="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-theme-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ strandLabels.ghost_row }}</p>
        <p class="mt-1 text-title-sm font-semibold text-gray-800 dark:text-white/90">{{ response.strand_counts.ghost_row }}</p>
      </div>
    </div>
  }

  @if (response) {
    <app-data-table
      (route)="handleRoute($event)"
      [title]="'Cases'"
      [dataPage]="response"
      [columns]="tableColumns"
      [rowActions]="rowActions"
      [selectionEnabled]="true"
      [selectedRowIds]="selectedRowIds"
      [loading]="loading"
      (selectionChange)="onSelectionChange($event)">
      <div header class="flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div class="flex w-full flex-col gap-3 sm:flex-row sm:items-end">
          <app-text-input [control]="searchControl" [placeholder]="'Search by event name or reference'"></app-text-input>
          <app-select [control]="strandControl" [choices]="strandChoices" [label]="''" [clearable]="false"></app-select>
          <app-select [control]="venueControl" [choices]="venueChoices$" [label]="''" [clearable]="false" [searchable]="true"></app-select>
        </div>
        <div class="flex w-full justify-end">
          <button
            type="button"
            (click)="bulkFix()"
            [disabled]="selectedCount === 0 || processing"
            class="h-12 text-theme-sm shadow-theme-xs inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 font-medium text-gray-700
              hover:bg-gray-50 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50
              dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
            title="Fix the selected cases. Ghost rows and approved overrides are never touched.">
            <lucide-icon [name]="FixIcon" [size]="16"></lucide-icon>
            Fix selected ({{ selectedCount }})
          </button>
        </div>
      </div>
    </app-data-table>

    <p class="mt-3 text-theme-xs text-gray-500 dark:text-gray-400">
      A bulk Fix only re-syncs cascade and hand-edited cases. Ghost rows must be reassigned, and approved overrides are protected.
    </p>
  }
</div>

<!-- TVG-176: Reassign ghost-row money onto a standard schedule row. -->
<app-modal
  [title]="'Reassign ghost-row money'"
  [show]="isReassignOpen"
  [closeButtonLabel]="'Cancel'"
  [confirmButtonLabel]="'Reassign'"
  [processing]="processing"
  [allowOverflow]="true"
  (onClose)="closeReassign()"
  (onConfirm)="confirmReassign()">
  @if (reassignCase) {
    <p class="mb-4 text-theme-sm text-gray-600 dark:text-gray-300">
      Move the {{ money(reassignCase.stored) }} keyed onto the ghost row of
      <strong>{{ reassignCase.event_name }}</strong> onto a standard schedule row, then re-run the calculation.
    </p>
    <app-select
      [control]="reassignTargetControl"
      [choices]="reassignChoices"
      [label]="'Schedule row'"
      [clearable]="false"></app-select>
  }
</app-modal>
